import { authHeaders } from './auth';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nexa-backend-red.vercel.app';

export interface BlogPost {
  _id: string;
  title: string;
  content: string;
  imageUrl?: string;
  views: number;
  category?: string;
  publishedAt: string;
}

export interface BlogResponse {
  data: BlogPost[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface SingleBlogResponse {
  data: BlogPost;
}

export interface BlogViewsResponse {
  id: string;
  title: string;
  views: number;
}

// Get all blog posts
export const getBlogPosts = async (params?: {
  category?: string;
  limit?: number;
  page?: number;
}): Promise<BlogResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());

    const url = `${API_BASE_URL}/api/blog${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch blog posts: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

// Get single blog post
export const getBlogPost = async (id: string): Promise<SingleBlogResponse> => {
  try {
    const url = `${API_BASE_URL}/api/blog/${id}`;
    console.log('Fetching blog post from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', response.status, errorText);
      throw new Error(`Failed to fetch blog post: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    if (!data || !data.data) {
      throw new Error('Invalid response format from server');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching blog post:', error);
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Please check your internet connection');
    }
    throw error;
  }
};

// Create blog post
export const createBlogPost = async (formData: FormData): Promise<SingleBlogResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/blog`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || `Failed to create blog post: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

// Update blog post
export const updateBlogPost = async (id: string, data: FormData | Partial<BlogPost>): Promise<SingleBlogResponse> => {
  try {
    const isFormData = data instanceof FormData;
    const response = await fetch(`${API_BASE_URL}/api/blog/${id}`, {
      method: 'PUT',
      headers: isFormData
        ? authHeaders()
        : { 'Content-Type': 'application/json', ...authHeaders() },
      body: isFormData ? data : JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || `Failed to update blog post: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

// Delete blog post
export const deleteBlogPost = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/blog/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || `Failed to delete blog post: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

// Get blog post views
export const getBlogPostViews = async (id: string): Promise<BlogViewsResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/blog/${id}/views`);

    if (!response.ok) {
      throw new Error(`Failed to fetch blog views: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching blog views:', error);
    throw error;
  }
};

